import { Col, Empty, Row, Table, Divider, Select } from "antd";
import { useSelector } from "react-redux";
import { UNITS } from "../../../../Common/Constants";
import { formatDateAndTime } from "../../../../Common/Utils";
import BATTERY from "../../../../../assets/images/icons/battery.svg";
import AddToCompare from "../addToCompare";
import CardCollapse from "../../../../Common/CardCollapse";
import { TEST_RIDE_SUMMARY } from "../../../../Common/Grafana/Constants";
import panelList from "../panelList.json";
import ChartFilter from "../../../../Common/ChartFilter";
import { useEffect, useState } from "react";
import PanelsRow from "../../../../Common/Grafana/PanelsRow";
import GetIframe from "../../../../Common/Grafana/GetIframe";

const BatteryPerformance = ({
  compareValue,
  updateCompareValue,
  baseUrl,
  baseFilterObject,
  batteryStackFilter,
  batteryCellFilter
}) => {
  const vehicleDetails = useSelector((state) => state.eol.vehicleDetails);
  const tripSummary = useSelector((state) => state.testRide.tripSummary);
  const [chartType, setChartType] = useState({
    timeSeries: true,
    histogram: false,
    boxPlot: false,
  });
  const [batteryList, setBatteryList] = useState([]);

  useEffect(() => {
    setBatteryList([panelList?.battery[0]]);
  }, []);

  const updateChartType = (name, value) => {
    setChartType((prev) => ({ ...prev, [name]: value }));
  };

  const onSelectBattery = (selectedValues) => {
    const selectedObjects = panelList?.battery?.filter((item) =>
      selectedValues.includes(item.var_name)
    );
    setBatteryList(selectedObjects);
  };

  const columns = [
    {
      title: "Start Time",
      dataIndex: "startTime",
      key: "startTime",
      render: (startTime) => formatDateAndTime(startTime),
    },
    {
      title: "End Time",
      dataIndex: "endTime",
      key: "endTime",
      render: (endTime) => formatDateAndTime(endTime),
    },
    {
      title: "Start Voltage",
      dataIndex: "startVoltage",
      key: "startVoltage",
      render: (startVoltage) => (
        <>
          {startVoltage}
          <span className="card-sub-text ml015">{UNITS.VOLTS}</span>
        </>
      ),
    },
    {
      title: "End Voltage",
      dataIndex: "endVoltage",
      key: "endVoltage",
      render: (endVoltage) => (
        <>
          {endVoltage}
          <span className="card-sub-text ml015">{UNITS.VOLTS}</span>
        </>
      ),
    },
    {
      title: "Starting Charge",
      dataIndex: "startSoc",
      key: "startSoc",
      render: (startSoc) => (
        <>
          {startSoc}
          <span className="card-sub-text ml015">{UNITS.PERCENT}</span>
        </>
      ),
    },
    {
      title: "Ending Charge",
      dataIndex: "endSoc",
      key: "endSoc",
      render: (endSoc) => (
        <>
          {endSoc}
          <span className="card-sub-text ml015">{UNITS.PERCENT}</span>
        </>
      ),
    },
    {
      title: "Distance",
      dataIndex: "distance",
      key: "distance",
      render: (distance) => (
        <>
          {distance}
          <span className="card-sub-text ml015">{UNITS.KM}</span>
        </>
      ),
    },
  ];

  const subContent = (
    <>
      <Row gutter={[16, 16]} justify="space-between" className="mt05">
        <Col xs={24} sm={24} md={16} lg={10} xl={10}>
          <Select
            value={batteryList?.map((item) => item.var_name)}
            mode="tags"
            maxTagCount={"responsive"}
            style={{ width: "100%" }}
            onChange={onSelectBattery}
            options={panelList?.battery?.map((i) => ({
              label: i.title,
              value: i.var_name,
            }))}
          />
        </Col>
        <Col xs={8} sm={6} md={4} lg={3} xl={3}>
          <ChartFilter
            updateChartType={updateChartType}
            chartType={chartType}
          />
        </Col>
      </Row>
      <Row className="mt105">
        <Col span={24}>
          {tripSummary?.batteryPerformance?.length > 0 ? (
            <Table
              columns={columns}
              dataSource={tripSummary?.batteryPerformance}
              rowKey={(record) => record.startTime}
              pagination={false}
              scroll={{ x: "100%" }}
            />
          ) : (
            <Empty />
          )}
        </Col>
      </Row>
      <div className="grey-box mt105">
        <Row gutter={[16, 16]} justify="center">
          <Col xs={24} sm={24} md={24} lg={8} xl={8} className="flex-center">
            Total Distance:
            <span className="ml025 bold">{tripSummary?.totalDistance}</span>
            <span className="ml025">{UNITS.KM}</span>
          </Col>
          <Col xs={24} sm={24} md={24} lg={8} xl={8} className="flex-center">
            Total Discharge:
            <span className="ml025 bold">{tripSummary?.totalDischarge}</span>
            <span className="ml025">{UNITS.PERCENT}</span>
          </Col>
        </Row>
      </div>
    </>
  );

  return (
    <CardCollapse
      cardClassName="ride-statistic-analytics-card"
      icon={BATTERY}
      title={"Battery Performance"}
      subContent={subContent}
    >
      <>
        <Row className="pd0">
          {batteryList?.map((panel, index) => (
            <Col key={index} span={24}>
              <PanelsRow
                title={panel.title}
                url={baseUrl}
                timeSeriesPanel={
                  chartType?.timeSeries &&
                  TEST_RIDE_SUMMARY.PANEL_ID[panel.var_name]["timeSeries"]
                }
                histogramPanel={
                  chartType?.histogram &&
                  TEST_RIDE_SUMMARY.PANEL_ID[panel.var_name]["histogram"]
                }
                boxPlotPanel={
                  chartType?.boxPlot &&
                  TEST_RIDE_SUMMARY.PANEL_ID[panel.var_name]["boxPlot"]
                }
                baseFilterObject={baseFilterObject}
                variableKeys={TEST_RIDE_SUMMARY.BASE_VARIABLES}
              />

              <Row className="mt1 mb1">
                <Col className="add-compare-col">
                  <AddToCompare
                    value={panel.var_name}
                    compareValue={compareValue}
                    updateCompareValue={updateCompareValue}
                  />
                </Col>
              </Row>
              {batteryList?.length !== index + 1 ? (
                <Divider className="compare-divider" />
              ) : null}
            </Col>
          ))}

          <Col span={24}>
            <Divider className="compare-divider" />
            {vehicleDetails?.imeiNumber && (
              <Row gutter={[12, 12]} className="pdtb05 mt1">
                <Col xs={24} sm={24} md={24} lg={8} xl={8} id="cell-voltages">
                  <GetIframe
                    title="Cell Voltages"
                    url={baseUrl}
                    panelId={TEST_RIDE_SUMMARY.PANEL_ID["cellVoltages"]}
                    variableObject={batteryCellFilter}
                    variableKeys={TEST_RIDE_SUMMARY.BASE_VARIABLES}
                    width="100%"
                    height="500"
                    divId="cell-voltages"
                  />
                </Col>
                <Col
                  xs={24}
                  sm={24}
                  md={24}
                  lg={8}
                  xl={8}
                  id="sensor-temp-boxplot"
                >
                  <GetIframe
                    title="Box Plot - Sensor Temperature"
                    url={baseUrl}
                    panelId={
                      TEST_RIDE_SUMMARY.PANEL_ID["batterySensor"]["boxPlot"]
                    }
                    variableObject={batteryStackFilter}
                    variableKeys={TEST_RIDE_SUMMARY.BASE_VARIABLES}
                    width="100%"
                    height="500"
                    divId="sensor-temp-boxplot"
                  />
                </Col>
                <Col xs={24} sm={24} md={24} lg={8} xl={8}>
                  <Row gutter={[12, 12]}>
                    <Col
                      xs={24}
                      sm={24}
                      md={12}
                      lg={24}
                      xl={24}
                      id="sensor-temp-timeseries"
                    >
                      <GetIframe
                        title="Time Series - Sensor Temperature"
                        url={baseUrl}
                        panelId={
                          TEST_RIDE_SUMMARY.PANEL_ID["batterySensor"][
                            "timeSeries"
                          ]
                        }
                        variableObject={batteryStackFilter}
                        variableKeys={TEST_RIDE_SUMMARY.BASE_VARIABLES}
                        width="100%"
                        height="240"
                        divId="sensor-temp-timeseries"
                      />
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={12}
                      lg={24}
                      xl={24}
                      id="sensor-temp-histogram"
                    >
                      <GetIframe
                        title="Histogram - Sensor Temperature"
                        url={baseUrl}
                        panelId={
                          TEST_RIDE_SUMMARY.PANEL_ID["batterySensor"][
                            "histogram"
                          ]
                        }
                        variableObject={batteryStackFilter}
                        variableKeys={TEST_RIDE_SUMMARY.BASE_VARIABLES}
                        width="100%"
                        height="240"
                        divId="sensor-temp-histogram"
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </>
    </CardCollapse>
  );
};

export default BatteryPerformance;
